const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const createIdempotencyKey = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

let isRedirectingOnExpire = false;

const clearAuthSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('businesses');
    localStorage.removeItem('selectedBusiness');
    localStorage.setItem('appLanguage', 'en');
    sessionStorage.removeItem('welcomeShown');
};

/** Decode JWT payload without verifying signature (client-side expiry check only). */
export const isTokenExpired = (token = localStorage.getItem('token')) => {
    if (!token) return true;
    try {
        const parts = token.split('.');
        if (parts.length < 2) return true;
        const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
        const payload = JSON.parse(atob(padded));
        if (!payload.exp) return false;
        // 10s clock-skew buffer
        return Date.now() >= payload.exp * 1000 - 10000;
    } catch {
        return true;
    }
};

export const handleTokenExpire = () => {
    if (isRedirectingOnExpire) return;
    const onLoginPage = window.location.pathname.includes('/login');
    clearAuthSession();
    window.dispatchEvent(new CustomEvent('authExpired'));
    if (onLoginPage) return;
    isRedirectingOnExpire = true;
    window.location.assign('/login?session=expired');
};

const isPublicApiRequest = (url, options = {}) => {
    const path = String(url).split('?')[0];
    const method = (options.method || 'GET').toUpperCase();
    if (path.includes('/auth/login')) return true;
    if (path.includes('/auth/logout')) return true;
    if (method === 'POST' && /\/v1\/user\/?$/.test(path)) return true;
    return false;
};

const expiredSessionResponse = () =>
    new Response(
        JSON.stringify({
            success: false,
            message: 'Session expired. Please login again.',
            expired: true
        }),
        { status: 401, headers: { 'Content-Type': 'application/json' } }
    );

/**
 * Fetch wrapper for API calls.
 * Proactively checks JWT expiry and redirects to login on 401/403 auth failures.
 */
export const apiFetch = async (url, options = {}) => {
    if (!isPublicApiRequest(url, options)) {
        const token = localStorage.getItem('token');
        if (!token || isTokenExpired(token)) {
            handleTokenExpire();
            return expiredSessionResponse();
        }
    }

    const response = await fetch(url, options);

    if (!isPublicApiRequest(url, options) && (response.status === 401 || response.status === 403)) {
        handleTokenExpire();
        return expiredSessionResponse();
    }

    return response;
};

export const login = async (email, password) => {
    try {
        const response = await apiFetch(`${BASE_URL}/auth/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
        });
        const data = await response.json();
        if (data.success) {
            // Store the token and user data in localStorage
            localStorage.setItem('token', data.data.token);
            localStorage.setItem('user', JSON.stringify(data.data));
            
            // Fetch and store business data immediately after login
            const userId = data.data.userId || data.data.id;
            if (userId) {
                try {
                    const businessResponse = await apiFetch(`${BASE_URL}/v1/business/by-user/${userId}`, {
                        method: 'GET',
                        headers: {
                            'Accept': 'application/json',
                            'x-user-id': userId,
                            ...getAuthHeader()
                        }
                    });
                    const businessData = await businessResponse.json();
                    if (businessData.success && Array.isArray(businessData.data)) {
                        localStorage.setItem('businesses', JSON.stringify(businessData.data));
                        if (businessData.data.length > 0) {
                            const selectedBusiness = businessData.data[0];
                            localStorage.setItem('selectedBusiness', JSON.stringify(selectedBusiness));
                            
                            // Fetch language setting from backend
                            try {
                                const languageResponse = await apiFetch(`${BASE_URL}/v1/setting/business/${selectedBusiness.id}/type/LANGUAGE`, {
                                    method: 'GET',
                                    headers: {
                                        'Accept': 'application/json',
                                        'x-user-id': userId,
                                        ...getAuthHeader()
                                    }
                                });
                                const languageData = await languageResponse.json();
                                
                                if (languageData.success && languageData.data && languageData.data.length > 0) {
                                    const languageSetting = languageData.data[0];
                                    // Convert backend language name to app language code
                                    // Backend can store 'ENGLISH', 'EN', 'en', 'HINDI', 'HI', 'hi'
                                    let appLanguage = 'en'; // default
                                    const langName = languageSetting.name ? languageSetting.name.toUpperCase() : '';
                                    
                                    if (langName === 'HINDI' || langName === 'HI') {
                                        appLanguage = 'hi';
                                    } else if (langName === 'ENGLISH' || langName === 'EN') {
                                        appLanguage = 'en';
                                    }
                                    
                                    console.log('Language setting from backend:', languageSetting.name, '-> Setting app language to:', appLanguage);
                                    localStorage.setItem('appLanguage', appLanguage);
                                    
                                    // Dispatch event to update language immediately
                                    window.dispatchEvent(new Event('languageChanged'));
                                } else {
                                    // No language setting found, default to English
                                    localStorage.setItem('appLanguage', 'en');
                                }
                            } catch (languageError) {
                                // If language fetch fails, default to English
                                console.error('Error fetching language setting:', languageError);
                                localStorage.setItem('appLanguage', 'en');
                            }
                        }
                        // Dispatch custom event to notify Layout component of update
                        window.dispatchEvent(new Event('localStorageUpdated'));
                    }
                } catch (businessError) {
                    // Silently fail - businesses will be loaded by Dashboard component
                    console.error('Error fetching businesses on login:', businessError);
                    // Default to English if business fetch fails
                    localStorage.setItem('appLanguage', 'en');
                }
            }
        }
        return data;
    } catch (error) {
        return {
            success: false,
            message: 'An error occurred during login',
            errors: { error: error.message }
        };
    }
};

export const createUser = async (userData) => {
    try {
        const response = await apiFetch(`${BASE_URL}/v1/user/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                firstName: userData.firstName,
                lastName: userData.lastName,
                email: userData.email,
                password: userData.password,
                role: 'USER'
            }),
        });
        return await response.json();
    } catch (error) {
        return {
            success: false,
            message: 'An error occurred during user creation',
            errors: { error: error.message }
        };
    }
};

export const updateUser = async (userId, userData) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const currentUserId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/user/${userId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'x-user-id': currentUserId
            },
            body: JSON.stringify({
                firstName: userData.firstName,
                lastName: userData.lastName,
                email: userData.email,
                ...(userData.password ? { password: userData.password } : {}),
                role: userData.role
            }),
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        return {
            success: false,
            message: 'An error occurred while updating user',
            errors: { error: error.message }
        };
    }
};

// Business APIs
export const createBusiness = async ({ userId, business }) => {
    try {
        const response = await apiFetch(`${BASE_URL}/v1/business/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader()
            },
            body: JSON.stringify({
                businessName: business.businessName,
                contactNumber: business.contactNumber,
                address: business.address,
                profileUrl: business.profileUrl,
                ...(business.email ? { email: business.email } : {})
            }),
        });
        return await response.json();
    } catch (error) {
        return {
            success: false,
            message: 'An error occurred during business creation',
            errors: { error: error.message }
        };
    }
};

export const updateBusiness = async (businessId, businessData) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/business/${businessId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'x-user-id': userId
            },
            body: JSON.stringify({
                businessName: businessData.businessName,
                contactNumber: businessData.contactNumber,
                address: businessData.address,
                ...(businessData.profileUrl ? { profileUrl: businessData.profileUrl } : {}),
                ...(businessData.email ? { email: businessData.email } : {})
            }),
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        return {
            success: false,
            message: 'An error occurred while updating business',
            errors: { error: error.message }
        };
    }
};

// Borrowers APIs
const ensureSelectedBusiness = async () => {
    const existing = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
    if (existing?.id) return existing;

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.userId || user.id;
    if (!userId) return null;

    const result = await getBusinessesByUser(userId);
    if (result?.success && Array.isArray(result.data) && result.data.length > 0) {
        localStorage.setItem('businesses', JSON.stringify(result.data));
        localStorage.setItem('selectedBusiness', JSON.stringify(result.data[0]));
        window.dispatchEvent(new Event('localStorageUpdated'));
        return result.data[0];
    }
    return null;
};

export const createBorrower = async (borrower) => {
    try {
        // Get business ID from localStorage (or reload from API after DB switch)
        let selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
        if (!selectedBusiness?.id) {
            selectedBusiness = await ensureSelectedBusiness();
        }
        const businessId = selectedBusiness?.id;
        
        if (!businessId) {
            return {
                success: false,
                message: 'No business selected. Please create or select a business first, then try again.',
                errors: { error: 'No business selected' }
            };
        }

        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        console.log('Creating borrower:', borrower);
        const response = await apiFetch(`${BASE_URL}/v1/borrower/business/${businessId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            },
            body: JSON.stringify({
                fullName: borrower.fullName,
                fatherName: borrower.fatherName,
                contactNumber: borrower.contactNumber,
                documentsUrl: borrower.documentsUrl || 'string', // Default as requested
                email: borrower.email,
                idProofNumber: borrower.idProofNumber,
                idProofType: borrower.idProofType,
                address: borrower.address
            }),
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Create borrower API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        console.log('Borrower created successfully:', data);
        return data;
    } catch (error) {
        console.error('Create borrower network error:', error);
        return {
            success: false,
            message: 'Network error occurred while creating borrower',
            errors: { error: error.message }
        };
    }
};

export const updateBorrower = async (id, borrower) => {
    try {
        // Get business ID from localStorage
        const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
        const businessId = selectedBusiness?.id;
        
        if (!businessId) {
            return {
                success: false,
                message: 'No business selected. Please select a business first.',
                errors: { error: 'No business selected' }
            };
        }

        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/borrower/business/${businessId}/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            },
            body: JSON.stringify({
                fullName: borrower.fullName,
                fatherName: borrower.fatherName,
                contactNumber: borrower.contactNumber,
                documentsUrl: borrower.documentsUrl || 'string',
                email: borrower.email,
                idProofNumber: borrower.idProofNumber,
                idProofType: borrower.idProofType,
                address: borrower.address
            }),
        });
        return await response.json();
    } catch (error) {
        return {
            success: false,
            message: 'An error occurred while updating borrower',
            errors: { error: error.message }
        };
    }
};

export const getBorrowers = async ({ page = 0, size = 20, sortBy = 'createdAt', sortDir = 'desc', search = '' } = {}) => {
    try {
        // Get business ID from localStorage
        const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
        const businessId = selectedBusiness?.id;
        
        if (!businessId) {
            return {
                success: false,
                message: 'No business selected. Please select a business first.',
                errors: { error: 'No business selected' }
            };
        }

        const params = new URLSearchParams({ page: String(page), size: String(size), sortBy, sortDir });
        if (search) params.append('search', search);
        
        console.log('Fetching borrowers with params:', { page, size, sortBy, sortDir, search });
        
        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/borrower/business/${businessId}?${params.toString()}`, {
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get borrowers API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        console.log('Borrowers fetched successfully:', data);
        return data;
    } catch (error) {
        console.error('Get borrowers network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching borrowers',
            errors: { error: error.message }
        };
    }
};

// Get borrower by ID
export const getBorrowerById = async (borrowerId) => {
    try {
        const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
        const businessId = selectedBusiness?.id;
        
        if (!businessId) {
            return {
                success: false,
                message: 'No business selected',
                errors: { error: 'No business selected' }
            };
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/borrower/business/${businessId}/${borrowerId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader()
            }
        });

        const data = await response.json();
        
        if (!response.ok) {
            return {
                success: false,
                message: data.message || 'Failed to fetch borrower',
                errors: data.errors || { error: 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get borrower by ID error:', error);
        return {
            success: false,
            message: 'Network error occurred',
            errors: { error: error.message }
        };
    }
};

// Get all loans for a borrower
export const getBorrowerLoans = async (borrowerId) => {
    try {
        const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
        const businessId = selectedBusiness?.id;
        
        if (!businessId) {
            return {
                success: false,
                message: 'No business selected',
                errors: { error: 'No business selected' }
            };
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/borrower/business/${businessId}/${borrowerId}/loans`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader()
            }
        });

        const data = await response.json();
        
        if (!response.ok) {
            return {
                success: false,
                message: data.message || 'Failed to fetch borrower loans',
                errors: data.errors || { error: 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get borrower loans error:', error);
        return {
            success: false,
            message: 'Network error occurred',
            errors: { error: error.message }
        };
    }
};

// Delete borrower
export const deleteBorrower = async (borrowerId) => {
    try {
        // Get business ID from localStorage
        const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
        const businessId = selectedBusiness?.id;
        
        if (!businessId) {
            return {
                success: false,
                message: 'No business selected. Please select a business first.',
                errors: { error: 'No business selected' }
            };
        }

        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/borrower/business/${businessId}/${borrowerId}`, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        return await response.json();
    } catch (error) {
        return {
            success: false,
            message: 'An error occurred while deleting borrower',
            errors: { error: error.message }
        };
    }
};

// Helper function to get the auth header (skips expired tokens)
export const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    if (!token) return {};
    if (isTokenExpired(token)) {
        handleTokenExpire();
        return {};
    }
    return { Authorization: `Bearer ${token}` };
};

export const logout = async () => {
    try {
        const token = localStorage.getItem('token');
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId;

        if (token && !isTokenExpired(token)) {
            await fetch(`${BASE_URL}/auth/logout`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'x-user-id': userId
                }
            });
        }
    } catch (error) {
        // Ignore logout API errors — always clear local session
    } finally {
        clearAuthSession();
    }

    return { success: true, message: 'Logged out successfully' };
};

// Business - Get by user
export const getBusinessesByUser = async (userId) => {
    try {
        const response = await apiFetch(`${BASE_URL}/v1/business/by-user/${userId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader()
            }
        });
        return await response.json();
    } catch (error) {
        return {
            success: false,
            message: 'An error occurred while fetching businesses by user',
            errors: { error: error.message }
        };
    }
};

// User-Business Link API
export const linkUserToBusiness = async ({ businessId, userId }) => {
    try {
        const response = await apiFetch(`${BASE_URL}/v1/user-business/${businessId}/link-user/${userId}`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader()
            }
        });
        return await response.json();
    } catch (error) {
        return {
            success: false,
            message: 'An error occurred while linking user to business',
            errors: { error: error.message }
        };
    }
};

// Loan APIs
export const createLoan = async (loanData) => {
    try {
        // Get business ID from localStorage
        const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
        const businessId = selectedBusiness?.id;
        
        if (!businessId) {
            return {
                success: false,
                message: 'No business selected. Please select a business first.',
                errors: { error: 'No business selected' }
            };
        }

        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/loan/business/${businessId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            },
            body: JSON.stringify(loanData),
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Create loan API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        console.log('Loan created successfully:', data);
        return data;
    } catch (error) {
        console.error('Create loan network error:', error);
        return {
            success: false,
            message: 'Network error occurred while creating loan',
            errors: { error: error.message }
        };
    }
};

export const getLoans = async ({
    page = 0,
    size = 20,
    sortBy = 'createdAt',
    sortDir = 'desc',
    search = '',
    status = '',
    collateralStatus = '',
    fromDate = '',
    toDate = '',
    year = '',
    month = '',
    date = ''
} = {}) => {
    try {
        // Get business ID from localStorage
        const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
        const businessId = selectedBusiness?.id;
        
        if (!businessId) {
            return {
                success: false,
                message: 'No business selected. Please select a business first.',
                errors: { error: 'No business selected' }
            };
        }

        const params = new URLSearchParams({ page: String(page), size: String(size), sortBy, sortDir });
        if (search) params.append('search', search);
        if (status && status !== 'All') params.append('status', status);
        if (collateralStatus && collateralStatus !== 'All') params.append('collateralStatus', collateralStatus);
        if (fromDate) params.append('fromDate', fromDate);
        if (toDate) params.append('toDate', toDate);
        if (year) params.append('year', String(year));
        if (month) params.append('month', String(month));
        if (date) params.append('date', date);
        
        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/loan/business/${businessId}?${params.toString()}`, {
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get loans API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get loans network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching loans',
            errors: { error: error.message }
        };
    }
};

export const getLoanById = async (loanId) => {
    try {
        if (!loanId) {
            return {
                success: false,
                message: 'Loan ID is required',
                errors: { error: 'Missing loan ID' }
            };
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/loan/${loanId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });

        const data = await response.json();

        if (!response.ok) {
            console.error('Get loan by ID API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });

            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }

        return data;
    } catch (error) {
        console.error('Get loan by ID network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching loan',
            errors: { error: error.message }
        };
    }
};

export const createLoanAdvance = async (loanId, advanceData) => {
    try {
        if (!loanId) {
            return { success: false, message: 'Loan ID is required', errors: { error: 'Missing loan ID' } };
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;
        const idempotencyKey = advanceData.idempotencyKey || createIdempotencyKey();
        const response = await apiFetch(`${BASE_URL}/v1/loan/${loanId}/advance`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'x-user-id': userId,
                'Idempotency-Key': idempotencyKey,
                ...getAuthHeader(),
            },
            body: JSON.stringify({
                amount: advanceData.amount,
                idempotencyKey,
                ...(advanceData.advanceDate ? { advanceDate: advanceData.advanceDate } : {})
            })
        });
        const data = await response.json();
        if (!response.ok) {
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        return data;
    } catch (error) {
        return {
            success: false,
            message: 'Network error occurred while creating loan advance',
            errors: { error: error.message }
        };
    }
};

export const updateLoan = async (loanId, loanData) => {
    try {
        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/loan/${loanId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            },
            body: JSON.stringify(loanData),
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Update loan API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        console.log('Loan updated successfully:', data);
        return data;
    } catch (error) {
        console.error('Update loan network error:', error);
        return {
            success: false,
            message: 'Network error occurred while updating loan',
            errors: { error: error.message }
        };
    }
};

export const deleteLoan = async (loanId) => {
    try {
        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/loan/${loanId}`, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Delete loan API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        console.log('Loan deleted successfully:', data);
        return data;
    } catch (error) {
        console.error('Delete loan network error:', error);
        return {
            success: false,
            message: 'Network error occurred while deleting loan',
            errors: { error: error.message }
        };
    }
};

export const updateLoanStatus = async (loanId, status) => {
    try {
        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/loan/${loanId}/status?status=${status}`, {
            method: 'PATCH',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Update loan status API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        console.log('Loan status updated successfully:', data);
        return data;
    } catch (error) {
        console.error('Update loan status network error:', error);
        return {
            success: false,
            message: 'Network error occurred while updating loan status',
            errors: { error: error.message }
        };
    }
};

// Dashboard APIs
export const getDashboardMetrics = async (businessId) => {
    try {
        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/dashboard/business/${businessId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get dashboard metrics API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get dashboard metrics network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching dashboard metrics',
            errors: { error: error.message }
        };
    }
};

export const getCollateralDashboard = async (businessId) => {
    try {
        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/collateral/dashboard/${businessId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get collateral dashboard API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get collateral dashboard network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching collateral dashboard',
            errors: { error: error.message }
        };
    }
};

export const getCollateralInHand = async (businessId) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/collateral/in-hand/${businessId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get in-hand collateral API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get in-hand collateral network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching in-hand collateral',
            errors: { error: error.message }
        };
    }
};

export const getCollateralPledged = async (businessId) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/collateral/pledged/${businessId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get pledged collateral API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get pledged collateral network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching pledged collateral',
            errors: { error: error.message }
        };
    }
};

export const getCollateralAwaiting = async (businessId) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/collateral/awaiting/${businessId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get awaiting collateral API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get awaiting collateral network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching awaiting collateral',
            errors: { error: error.message }
        };
    }
};

// Pledge APIs
export const createPledge = async (pledgeData) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/pledge/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            },
            body: JSON.stringify(pledgeData)
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Create pledge API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Create pledge network error:', error);
        return {
            success: false,
            message: 'Network error occurred while creating pledge',
            errors: { error: error.message }
        };
    }
};

export const getActivePledges = async (businessId) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/pledge/business/${businessId}/active`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get active pledges API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get active pledges network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching active pledges',
            errors: { error: error.message }
        };
    }
};

export const getPledges = async ({
    page = 0,
    size = 20,
    search = '',
    collateralStatus = '',
    fromDate = '',
    toDate = '',
    year = '',
    month = '',
    date = ''
} = {}) => {
    try {
        const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
        const businessId = selectedBusiness?.id;
        if (!businessId) {
            return {
                success: false,
                message: 'No business selected. Please select a business first.',
                errors: { error: 'No business selected' }
            };
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('size', String(size));
        if (search && search.trim()) params.set('search', search.trim());
        if (collateralStatus && collateralStatus !== 'ALL') {
            params.set('collateralStatus', collateralStatus);
        }
        if (date) params.set('date', date);
        if (year) params.set('year', String(year));
        if (month) params.set('month', String(month));
        if (fromDate) params.set('fromDate', fromDate);
        if (toDate) params.set('toDate', toDate);

        const response = await apiFetch(
            `${BASE_URL}/v1/pledge/business/${businessId}?${params.toString()}`,
            {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'x-user-id': userId,
                    ...getAuthHeader(),
                }
            }
        );

        const data = await response.json();

        if (!response.ok) {
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }

        return data;
    } catch (error) {
        console.error('Get pledges network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching pledges',
            errors: { error: error.message }
        };
    }
};

export const getPledgeBankSummary = async () => {
    try {
        const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
        const businessId = selectedBusiness?.id;
        if (!businessId) {
            return {
                success: false,
                message: 'No business selected. Please select a business first.',
                errors: { error: 'No business selected' }
            };
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(
            `${BASE_URL}/v1/pledge/business/${businessId}/summary/by-bank`,
            {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'x-user-id': userId,
                    ...getAuthHeader(),
                }
            }
        );

        const data = await response.json();
        if (!response.ok) {
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        return data;
    } catch (error) {
        console.error('Get pledge bank summary network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching bank summary',
            errors: { error: error.message }
        };
    }
};

export const updateLoanCollateralStatus = async (loanId, status) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/loan/${loanId}/collateral-status?status=${status}`, {
            method: 'PATCH',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Update collateral status API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Update collateral status network error:', error);
        return {
            success: false,
            message: 'Network error occurred while updating collateral status',
            errors: { error: error.message }
        };
    }
};

export const getPledgesByLoan = async (businessId, loanId) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/pledge/business/${businessId}/loan/${loanId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get pledges by loan API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get pledges by loan network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching pledges',
            errors: { error: error.message }
        };
    }
};

export const getPledgeById = async (businessId, pledgeId) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/pledge/business/${businessId}/pledge/${pledgeId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get pledge by ID API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get pledge by ID network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching pledge',
            errors: { error: error.message }
        };
    }
};

export const getPledgeInterest = async (businessId, pledgeId) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(
            `${BASE_URL}/v1/pledge/business/${businessId}/pledge/${pledgeId}/interest`,
            {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'x-user-id': userId,
                    ...getAuthHeader(),
                }
            }
        );

        const data = await response.json();
        if (!response.ok) {
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        return data;
    } catch (error) {
        console.error('Get pledge interest network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching pledge interest',
            errors: { error: error.message }
        };
    }
};

export const payPledgeInterest = async (businessId, pledgeId, paymentDate = null) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;
        const body = paymentDate ? { paymentDate } : {};

        const response = await apiFetch(
            `${BASE_URL}/v1/pledge/business/${businessId}/pledge/${pledgeId}/interest/pay`,
            {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'x-user-id': userId,
                    ...getAuthHeader(),
                },
                body: JSON.stringify(body)
            }
        );

        const data = await response.json();
        if (!response.ok) {
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        return data;
    } catch (error) {
        console.error('Pay pledge interest network error:', error);
        return {
            success: false,
            message: 'Network error occurred while paying pledge interest',
            errors: { error: error.message }
        };
    }
};

export const getOverdueLoans = async (businessId) => {
    try {
        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/loan/overdue/${businessId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get overdue loans API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get overdue loans network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching overdue loans',
            errors: { error: error.message }
        };
    }
};

export const getExpiringLoans = async (businessId, days = 30) => {
    try {
        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/loan/expiring/${businessId}?days=${days}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get expiring loans API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get expiring loans network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching expiring loans',
            errors: { error: error.message }
        };
    }
};

// Setting APIs (for both LOAN and PLEDGE types)
export const createSetting = async (businessId, settingData) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/setting/business/${businessId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            },
            body: JSON.stringify(settingData)
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Create setting API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Create setting network error:', error);
        return {
            success: false,
            message: 'Network error occurred while creating setting',
            errors: { error: error.message }
        };
    }
};

export const getSettingsByType = async (businessId, type) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/setting/business/${businessId}/type/${type}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get settings API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get settings network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching settings',
            errors: { error: error.message }
        };
    }
};

export const updateSetting = async (businessId, settingId, settingData) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/setting/business/${businessId}/setting/${settingId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            },
            body: JSON.stringify(settingData)
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Update setting API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Update setting network error:', error);
        return {
            success: false,
            message: 'Network error occurred while updating setting',
            errors: { error: error.message }
        };
    }
};

export const deleteSetting = async (businessId, settingId) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/setting/business/${businessId}/setting/${settingId}`, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Delete setting API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Delete setting network error:', error);
        return {
            success: false,
            message: 'Network error occurred while deleting setting',
            errors: { error: error.message }
        };
    }
};

// Legacy function names for backward compatibility (will be removed later)
export const createRePledgeSetting = async (businessId, settingData) => {
    return createSetting(businessId, settingData);
};

export const getRePledgeSettings = async (businessId) => {
    return getSettingsByType(businessId, 'PLEDGE');
};

export const updateRePledgeSetting = async (businessId, settingId, settingData) => {
    return updateSetting(businessId, settingId, settingData);
};

export const deleteRePledgeSetting = async (businessId, settingId) => {
    return deleteSetting(businessId, settingId);
};

// Payment APIs
export const createPayment = async (paymentData) => {
    try {
        // Get business ID from localStorage
        const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
        const businessId = selectedBusiness?.id;
        
        if (!businessId) {
            return {
                success: false,
                message: 'No business selected. Please select a business first.',
                errors: { error: 'No business selected' }
            };
        }

        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;
        const idempotencyKey = paymentData.idempotencyKey || createIdempotencyKey();

        const response = await apiFetch(`${BASE_URL}/v1/payment/business/${businessId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'x-user-id': userId,
                'Idempotency-Key': idempotencyKey,
                ...getAuthHeader(),
            },
            body: JSON.stringify({
                businessId: businessId,
                borrowerId: paymentData.borrowerId,
                loanId: paymentData.loanId,
                amount: paymentData.amount,
                idempotencyKey,
                ...(paymentData.paymentType ? { paymentType: paymentData.paymentType } : {}),
                ...(paymentData.roundOfAmount !== undefined ? { roundOfAmount: paymentData.roundOfAmount } : {}),
                paymentMethod: paymentData.paymentMethod,
                ...(paymentData.description ? { description: paymentData.description } : {}),
                ...(paymentData.referenceNumber ? { referenceNumber: paymentData.referenceNumber } : {})
            }),
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Create payment API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        console.log('Payment created successfully:', data);
        return data;
    } catch (error) {
        console.error('Create payment network error:', error);
        return {
            success: false,
            message: 'Network error occurred while creating payment',
            errors: { error: error.message }
        };
    }
};

export const getPaymentsByLoan = async (businessId, loanId) => {
    try {
        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/payment/business/${businessId}/loan/${loanId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get payments by loan API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get payments by loan network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching payments',
            errors: { error: error.message }
        };
    }
};

export const getPaymentById = async (businessId, paymentId) => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const response = await apiFetch(`${BASE_URL}/v1/payment/business/${businessId}/payment/${paymentId}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });

        const data = await response.json();

        if (!response.ok) {
            console.error('Get payment by ID API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });

            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }

        return data;
    } catch (error) {
        console.error('Get payment by ID network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching payment',
            errors: { error: error.message }
        };
    }
};

// Profitability Analytics APIs
export const getProfitabilityOverview = async (businessId, range = 'MONTH', year = null) => {
    try {
        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        const params = new URLSearchParams();
        if (range) params.append('range', range);
        if (year !== undefined && year !== null) params.append('year', year);
        const queryString = params.toString();

        const response = await apiFetch(
            `${BASE_URL}/v1/analytics/profitability/${businessId}${queryString ? `?${queryString}` : ''}`,
            {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get profitability overview API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get profitability overview network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching profitability overview',
            errors: { error: error.message }
        };
    }
};

export const getMonthlyProfitability = async (businessId, month, year, range = 'MONTH') => {
    try {
        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        // Build query parameters
        const params = new URLSearchParams();
        if (month !== undefined && month !== null) {
            params.append('month', month);
        }
        if (year !== undefined && year !== null) {
            params.append('year', year);
        }
        if (range) {
            params.append('range', range);
        }

        const queryString = params.toString();
        const url = `${BASE_URL}/v1/analytics/profitability/${businessId}/monthly${queryString ? `?${queryString}` : ''}`;

        const response = await apiFetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get monthly profitability API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get monthly profitability network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching monthly profitability',
            errors: { error: error.message }
        };
    }
};

// Analytics & Reports API
export const getAnalyticsReport = async (businessId, days = 90) => {
    try {
        // Get user ID for x-user-id header
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userId = user.userId || user.id;

        // Build query parameters
        const params = new URLSearchParams();
        if (days !== undefined && days !== null) {
            params.append('days', days);
        }

        const queryString = params.toString();
        const url = `${BASE_URL}/v1/analytics/reports/${businessId}${queryString ? `?${queryString}` : ''}`;

        const response = await apiFetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'x-user-id': userId,
                ...getAuthHeader(),
            }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
            console.error('Get analytics report API error:', {
                status: response.status,
                statusText: response.statusText,
                data
            });
            
            return {
                success: false,
                message: data.message || `Server error: ${response.status} ${response.statusText}`,
                errors: data.errors || { error: data.message || 'Unknown error' }
            };
        }
        
        return data;
    } catch (error) {
        console.error('Get analytics report network error:', error);
        return {
            success: false,
            message: 'Network error occurred while fetching analytics report',
            errors: { error: error.message }
        };
    }
};
